import React, { useCallback, useEffect, useRef, useState } from 'react';
import AtableLogo from '../shared/design/components/AtableLogo';
import { Button } from '../shared/design';
import { firestoreService } from '../shared/services/firestoreService';
import { auth } from '../shared/services/firebase';
import { USE_FIREBASE } from '../shared/services/apiConfig';
import { userMessage } from '../shared/userFacingError';
import {
    RESTAURATEUR_CONTACT_EMAIL,
    formatPhone,
    formatSiret,
    type RestaurateurApplication,
    type RestaurateurApplicationStatus,
} from '../shared/restaurateurApplication';
import { SpinnerIcon } from '../restaurateur/components/icons/SpinnerIcon';
import { CheckIcon, ClockIcon, MailIcon, WarningIcon } from '../restaurateur/components/icons/UiIcons';
import ClientMobileRedirect from './ClientMobileRedirect';
import RestaurateurSignupForm from './RestaurateurSignupForm';
import type { User } from '../shared/types';

/**
 * Utilisateur CLIENT sur le web : s'il a fait une demande d'accès restaurateur,
 * on affiche son état (en attente, refusée, acceptée) ; sinon l'écran habituel
 * « L'app atable est sur mobile », d'où il peut déposer une demande avec son
 * compte. Si la demande ne peut pas être lue (règles pas encore déployées…),
 * on retombe sur l'écran habituel.
 */
const RestaurateurApplicationGate: React.FC<{ user: User }> = ({ user }) => {
    const [state, setState] = useState<{ uid: string; application: RestaurateurApplication | null } | null>(null);
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        if (!USE_FIREBASE) return;
        return firestoreService.subscribeToMyRestaurateurApplication(
            user.id,
            (application) => setState({ uid: user.id, application }),
            () => setState({ uid: user.id, application: null }),
        );
    }, [user.id]);

    // Demande enregistrée : l'écran d'attente remplace le formulaire.
    useEffect(() => {
        if (state?.application) setApplying(false);
    }, [state?.application]);

    if (!USE_FIREBASE) return <ClientMobileRedirect />;
    if (!state || state.uid !== user.id) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-gray-50" role="status" aria-label="Chargement">
                <SpinnerIcon className="w-10 h-10 text-brand-primary" />
            </div>
        );
    }
    if (!state.application) {
        return applying
            ? <ApplyWithExistingAccount email={auth.currentUser?.email ?? user.email} onCancel={() => setApplying(false)} />
            : <ClientMobileRedirect onRequestRestaurateurAccess={() => setApplying(true)} />;
    }
    return <RestaurateurApplicationStatus application={state.application} />;
};

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const handleLogout = async () => {
    await firestoreService.logout();
    window.location.reload();
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-cream dark:bg-gray-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
            <div className="flex justify-center mb-8">
                <AtableLogo variant="lockup" height={120} />
            </div>
            <main className="bg-white dark:bg-gray-900 rounded-2xl border border-cream-200 dark:border-gray-800 shadow-md p-8 space-y-6">
                {children}
            </main>
        </div>
    </div>
);

/** Client déjà connecté : demande d'accès avec son compte (sans nouveau compte). */
const ApplyWithExistingAccount: React.FC<{ email: string; onCancel: () => void }> = ({ email, onCancel }) => {
    const titleRef = useRef<HTMLHeadingElement>(null);
    useEffect(() => titleRef.current?.focus(), []);
    return (
        <Layout>
            <h1 ref={titleRef} tabIndex={-1} className="text-2xl font-bold text-gray-900 dark:text-gray-100 focus:outline-none">
                Demander un accès professionnel
            </h1>
            <RestaurateurSignupForm mode="existingAccount" accountEmail={email} onCancel={onCancel} />
        </Layout>
    );
};

const STATUS_ANNOUNCEMENTS: Record<RestaurateurApplicationStatus, string> = {
    pending: 'Votre demande est en cours de vérification.',
    rejected: "Votre demande d'accès restaurateur n'a pas été retenue.",
    approved: "Votre demande d'accès restaurateur a été acceptée.",
};

const RestaurateurApplicationStatus: React.FC<{ application: RestaurateurApplication }> = ({ application }) => {
    const { status } = application;
    // Changement de statut pendant que l'écran est ouvert : annoncé aux lecteurs d'écran.
    const [announcement, setAnnouncement] = useState('');
    const previousStatus = useRef(status);
    useEffect(() => {
        if (previousStatus.current !== status) setAnnouncement(STATUS_ANNOUNCEMENTS[status]);
        previousStatus.current = status;
    }, [status]);

    return (
        <Layout>
            <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
            {status === 'pending' && <PendingContent application={application} />}
            {status === 'rejected' && <RejectedContent application={application} />}
            {status === 'approved' && <ApprovedContent />}

            <div className="pt-4 border-t border-cream-200 dark:border-gray-800 text-center">
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                    Se déconnecter
                </Button>
            </div>
        </Layout>
    );
};

const StatusIcon: React.FC<{ tone: 'brand' | 'warning' | 'success'; children: React.ReactNode }> = ({ tone, children }) => {
    const tones = {
        brand: 'bg-brand-50 text-brand-600 dark:bg-gray-800 dark:text-brand-300',
        warning: 'bg-warning-bg text-warning',
        success: 'bg-success-bg text-success',
    };
    return (
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${tones[tone]}`} aria-hidden="true">
            {children}
        </div>
    );
};

const PendingContent: React.FC<{ application: RestaurateurApplication }> = ({ application }) => (
    <>
        <StatusIcon tone="brand"><ClockIcon className="w-8 h-8" /></StatusIcon>
        <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Demande reçue — nous vérifions vos informations
            </h1>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Merci {application.firstName} ! Dès que notre équipe aura validé votre établissement,
                votre espace restaurateur s'ouvrira ici, sur cette page.
            </p>
        </div>

        <EmailVerification application={application} />

        <dl className="text-sm bg-gray-50 dark:bg-gray-950 rounded-xl p-4 space-y-2">
            <div className="flex justify-between gap-4">
                <dt className="text-gray-600 dark:text-gray-400">Restaurant</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100 text-right">{application.restaurantName}</dd>
            </div>
            <div className="flex justify-between gap-4">
                <dt className="text-gray-600 dark:text-gray-400">Adresse</dt>
                <dd className="text-gray-900 dark:text-gray-100 text-right">{application.address}</dd>
            </div>
            <div className="flex justify-between gap-4">
                <dt className="text-gray-600 dark:text-gray-400">SIRET</dt>
                <dd className="text-gray-900 dark:text-gray-100 text-right tabular-nums">{formatSiret(application.siret)}</dd>
            </div>
            <div className="flex justify-between gap-4">
                <dt className="text-gray-600 dark:text-gray-400">Téléphone</dt>
                <dd className="text-gray-900 dark:text-gray-100 text-right tabular-nums">{formatPhone(application.phone)}</dd>
            </div>
            <div className="flex justify-between gap-4">
                <dt className="text-gray-600 dark:text-gray-400">Demande envoyée le</dt>
                <dd className="text-gray-900 dark:text-gray-100 text-right">{formatDate(application.createdAt)}</dd>
            </div>
        </dl>

        <WithdrawApplication />
    </>
);

/**
 * État de vérification de l'e-mail : relu auprès de Firebase Auth à l'ouverture,
 * au retour sur l'onglet (après un clic sur le lien reçu) et à la demande.
 */
const EmailVerification: React.FC<{ application: RestaurateurApplication }> = ({ application }) => {
    const [verified, setVerified] = useState(application.emailVerified);
    const [busy, setBusy] = useState<'check' | 'resend' | null>(null);
    const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
    // Résultat réel de l'envoi automatique (inscription) : on n'affirme pas qu'un
    // e-mail est parti s'il a échoué. null : inconnu (page rechargée depuis).
    const [emailStatus, setEmailStatus] = useState(() => firestoreService.getVerificationEmailStatus());
    // Après « J'ai confirmé mon adresse », le bouton disparaît : le focus va sur le
    // message de confirmation (lu par les lecteurs d'écran) au lieu de se perdre.
    const confirmationRef = useRef<HTMLParagraphElement>(null);
    const focusConfirmation = useRef(false);

    useEffect(() => {
        if (application.emailVerified) setVerified(true);
    }, [application.emailVerified]);

    useEffect(() => {
        if (verified && focusConfirmation.current) {
            focusConfirmation.current = false;
            confirmationRef.current?.focus();
        }
    }, [verified]);

    const refresh = useCallback(async (manual: boolean) => {
        if (manual) {
            setBusy('check');
            setMessage(null);
            // Avant l'appel : la demande (écoutée en temps réel) peut passer à
            // « vérifiée » pendant celui-ci.
            focusConfirmation.current = true;
        }
        let isVerified = false;
        try {
            isVerified = await firestoreService.refreshApplicantEmailVerification();
            if (isVerified) {
                setVerified(true);
            } else if (manual) {
                setMessage({ tone: 'info', text: "Votre adresse n'est pas encore confirmée : cliquez sur le lien reçu par e-mail (pensez à regarder dans vos courriers indésirables)." });
            }
        } catch (error) {
            if (manual) setMessage({ tone: 'error', text: userMessage(error, 'Vérification impossible pour le moment. Réessayez dans quelques instants.') });
        } finally {
            if (manual) {
                setBusy(null);
                if (!isVerified) focusConfirmation.current = false;
            }
        }
    }, []);

    useEffect(() => {
        if (verified) return;
        refresh(false);
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh(false);
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [verified, refresh]);

    const resend = async () => {
        setBusy('resend');
        setMessage(null);
        try {
            await firestoreService.sendApplicantVerificationEmail();
            setEmailStatus('sent');
            setMessage({ tone: 'info', text: `E-mail envoyé à ${application.email}.` });
        } catch (error) {
            setMessage({ tone: 'error', text: userMessage(error, "L'e-mail n'a pas pu être envoyé. Réessayez dans quelques instants.") });
        } finally {
            setBusy(null);
        }
    };

    if (verified) {
        return (
            <p
                ref={confirmationRef}
                tabIndex={-1}
                className="flex items-center gap-2 text-sm font-medium bg-success-bg text-success rounded-xl px-4 py-3 focus:outline-none"
            >
                <CheckIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                Adresse e-mail confirmée : {application.email}
            </p>
        );
    }

    return (
        <section aria-labelledby="email-verification-title" className="bg-warning-bg rounded-xl px-4 py-4 space-y-3">
            <h2 id="email-verification-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <MailIcon className="w-5 h-5 flex-shrink-0 text-warning" aria-hidden="true" />
                Confirmez votre adresse e-mail
            </h2>
            <p className="text-sm text-gray-700">
                {emailStatus === 'sent' && (
                    <>Nous avons envoyé un lien de confirmation à <span className="font-medium">{application.email}</span>. Cliquez dessus : votre demande ne peut pas être validée avant.</>
                )}
                {emailStatus === 'failed' && (
                    <>L'e-mail de confirmation n'a pas pu être envoyé à <span className="font-medium">{application.email}</span>. Utilisez « Envoyer l'e-mail » ci-dessous : votre demande ne peut pas être validée sans cette confirmation.</>
                )}
                {emailStatus === null && (
                    <>Confirmez <span className="font-medium">{application.email}</span> avec le lien de confirmation reçu par e-mail : votre demande ne peut pas être validée avant. Rien reçu ? Regardez dans vos courriers indésirables ou demandez un nouvel e-mail.</>
                )}
            </p>
            <div className="flex flex-wrap gap-2">
                <Button variant="primary" size="sm" onClick={() => refresh(true)} loading={busy === 'check'} disabled={busy !== null}>
                    J'ai confirmé mon adresse
                </Button>
                <Button variant="outline" size="sm" onClick={resend} loading={busy === 'resend'} disabled={busy !== null}>
                    {emailStatus === 'failed' ? "Envoyer l'e-mail" : "Renvoyer l'e-mail"}
                </Button>
            </div>
            <p role="status" aria-live="polite" className={`text-sm ${message?.tone === 'error' ? 'text-red-700' : 'text-gray-700'}`}>
                {message?.text}
            </p>
        </section>
    );
};

/** Retrait de la demande en attente, avec confirmation. */
const WithdrawApplication: React.FC = () => {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const confirmRef = useRef<HTMLButtonElement>(null);
    const openerRef = useRef<HTMLButtonElement>(null);
    const returnFocus = useRef(false);

    useEffect(() => {
        if (confirming) confirmRef.current?.focus();
        else if (returnFocus.current) {
            returnFocus.current = false;
            openerRef.current?.focus();
        }
    }, [confirming]);

    const withdraw = async () => {
        setBusy(true);
        setError(null);
        try {
            await firestoreService.withdrawMyRestaurateurApplication();
            // Demande supprimée : l'écran « L'app atable est sur mobile » s'affiche.
        } catch (err) {
            setError(userMessage(err, "Votre demande n'a pas pu être retirée. Réessayez dans quelques instants."));
            setBusy(false);
        }
    };

    const cancel = () => {
        returnFocus.current = true;
        setConfirming(false);
        setError(null);
    };

    if (!confirming) {
        return (
            <div className="text-center">
                <button
                    ref={openerRef}
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="text-sm text-gray-600 dark:text-gray-400 underline hover:text-gray-900 dark:hover:text-gray-100"
                >
                    Retirer ma demande
                </button>
            </div>
        );
    }

    return (
        <div role="group" aria-labelledby="withdraw-title" className="bg-gray-50 dark:bg-gray-950 rounded-xl p-4 space-y-3">
            <p id="withdraw-title" className="text-sm text-gray-800 dark:text-gray-200">
                Retirer votre demande ? Les informations saisies seront supprimées ; vous pourrez en déposer une nouvelle.
            </p>
            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={cancel} disabled={busy}>
                    Garder ma demande
                </Button>
                <button
                    ref={confirmRef}
                    type="button"
                    onClick={withdraw}
                    disabled={busy}
                    className="px-3.5 py-1.5 text-sm rounded-xl font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                >
                    {busy ? 'Retrait…' : 'Oui, retirer ma demande'}
                </button>
            </div>
        </div>
    );
};

const RejectedContent: React.FC<{ application: RestaurateurApplication }> = ({ application }) => (
    <>
        <StatusIcon tone="warning"><WarningIcon className="w-8 h-8" /></StatusIcon>
        <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Demande non retenue
            </h1>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Votre demande d'accès restaurateur pour {application.restaurantName} n'a pas été acceptée
                {application.decidedAt ? ` (le ${formatDate(application.decidedAt)})` : ''}.
            </p>
        </div>
        {application.rejectionReason && (
            <div className="bg-gray-50 dark:bg-gray-950 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Motif</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{application.rejectionReason}</p>
            </div>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Si votre situation a changé ou s'il s'agit d'une erreur, contactez l'équipe atable
            {RESTAURATEUR_CONTACT_EMAIL ? (
                <> : <a href={`mailto:${encodeURIComponent(RESTAURATEUR_CONTACT_EMAIL)}`} className="font-medium underline">{RESTAURATEUR_CONTACT_EMAIL}</a>.</>
            ) : '.'}
        </p>
    </>
);

/**
 * Demande acceptée mais profil encore CLIENT : normalement transitoire (le profil
 * est mis à jour dans la même transaction et écouté en temps réel).
 */
const ApprovedContent: React.FC = () => (
    <>
        <StatusIcon tone="success"><CheckIcon className="w-8 h-8" /></StatusIcon>
        <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Demande acceptée
            </h1>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Votre espace restaurateur est prêt. S'il ne s'ouvre pas dans quelques secondes,
                rechargez la page ; si cela ne suffit pas, déconnectez-vous puis reconnectez-vous.
            </p>
        </div>
        <div className="flex justify-center">
            <Button variant="primary" onClick={() => window.location.reload()}>
                Recharger la page
            </Button>
        </div>
    </>
);

export default RestaurateurApplicationGate;
